<script setup lang="ts">
import type { ConcreteComponent, ComputedOptions, MethodOptions, FunctionalComponent } from 'vue';
import MIcon from '../MIcon/MIcon.vue';

const slots = useSlots();
const props = withDefaults(defineProps<{
    element?: 'a' | 'button' | 'div' | 'span' | ConcreteComponent<{}, any, any, ComputedOptions, MethodOptions> | FunctionalComponent<{}, any, any> | string;
    href?: string;
    type?: 'upsell' | 'primary' | 'secondary' | 'tertiary';
    value?: string;
    iconLeft?: string;
    iconRight?: string;
    disabled?: boolean;
    loading?: boolean | string;
    small?: boolean;
    target?: '_blank' | '_self' | '_parent' | '_top';
  }>(), {
  element: 'a',
  href: undefined,
  dismissible: true,
  type: 'primary',
  iconLeft: undefined,
  iconRight: undefined,
  value: undefined,
  loading: false,
  target: undefined,
});

const { href, type, value, iconLeft, iconRight, disabled, loading, small } = toRefs(props);

const isIconOnly = computed(() => !props.value && !slots.default);
</script>

<template>
  <Component
    :is="element"
    class="m-button m-button-cta"
    v-bind="{href, disabled}"
    :class="{
      'primary': type === 'primary',
      'secondary': type === 'secondary',
      'tertiary': type === 'tertiary',
      'upsell': type === 'upsell',
      'disabled': disabled,
      'icon-only': isIconOnly,
      [small ? 'small' : 'medium']: true
    }"
    :target="target"
  >
    <template v-if="loading">
      <MIcon
        icon="spinner"
        class="icon-left"
      />
      {{typeof loading === 'string' ? loading : 'Loading'}}
    </template>
    <template v-else>
      <MIcon
        v-if="iconLeft"
        :icon="iconLeft"
        class="icon-left"
      />

      <template v-if="value">
        {{value}}
      </template>
      <slot v-else-if="$slots.default" />
      <template v-else>
        &ZeroWidthSpace;
      </template>

      <MIcon
        v-if="iconRight"
        :icon="iconRight"
        class="icon-right"
      />
    </template>
  </Component>
</template>

<style lang="scss">
  @import '../../assets/styles/font-styles.scss';
  @import '@mui/assets/styles/breakpoints.scss';

  .m-button-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: calc(0.5em - 2px) calc(1.25em - 2px);
    border-radius: 4em;
    transition: all 0.2s ease-out;
    color: var(--palette-neutral-80);
    border: 2px solid transparent;
    user-select: none;
    text-decoration: none;
    cursor: pointer;

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;

      pointer-events: none;
    }

    &.medium {
      @include f-22-bold;
    }

    &.small {
      @include f-16-bold;
    }

    &.upsell {
      background-color: var(--palette-yellow-50);
      border-color: var(--palette-yellow-50);

      &:hover {
        background-color: var(--palette-yellow-50);
        border-color: var(--palette-yellow-50);
      }
    }

    &.primary {
      background-color: var(--palette-blue-20);
      border-color: var(--palette-blue-20);

      &:hover {
        background-color: var(--palette-blue-10);
        border-color: var(--palette-blue-10);
      }
    }

    &.secondary {
      border-color: var(--palette-neutral-80);

      &:hover {
        background-color: var(--palette-neutral-80);
        border-color: var(--palette-neutral-80);
        color: var(--palette-neutral-10);
      }
    }

    &.icon-only {
      padding: calc(0.5em - 2px);
      width: 2.5em;

      .m-icon {
        margin: 0;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .icon-left {
      margin-right: .75em;
      @include breakpoint-down('medium') {
        width: 1rem;
      }
    }

    .icon-right {
      margin-left: .75em;
    }
  }
</style>